import { useMemo } from "react";
import { getBlocksByTag, getBlocksByKind } from "@louise/content";
import type { ContentBlock, ContentTag } from "@louise/content";
import { GRANTS } from "../costs/grants.data";
import THERAPY_ILLUSTRATIONS from "../../../assets/images/therapies";
import type { SearchItem } from "./searchTypes";
import { getMetricFactsheets } from "../../data/metricFactsheets";
import { getTestRouteTarget } from "../access/testRouteIds";
import { EMBER } from "../../../onboarding/theme";
import { useLocale, useTranslations } from "@src/packages/i18n";

// ── Supplement bottle images (same logic as SupplementsScreen) ──
const SUPPLEMENT_BOTTLES = [
  require("../../../assets/images/supplement-bottle.png"),
  require("../../../assets/images/supplement-bottle-2.png"),
  require("../../../assets/images/supplement-bottle-3.png"),
];

// ── Document category images (same as DocumentsVaultCard) ──
const DOC_CATEGORIES = [
  {
    key: "medical_records",
    labelKey: "documents.category.medical_records.label",
    image: require("../../screens/care/assets/icon-folder.webp"),
  },
  {
    key: "coverage_benefits",
    labelKey: "documents.category.coverage_benefits.label",
    image: require("../../screens/care/assets/icon-shield.webp"),
  },
  {
    key: "admin",
    labelKey: "documents.category.admin.label",
    image: require("../../screens/care/assets/icon-receipt.webp"),
  },
  {
    key: "legal_consent",
    labelKey: "documents.category.legal_consent.label",
    image: require("../../screens/care/assets/icon-signature.webp"),
  },
] as const;

/** Stable bottle index from supplement ID — matches SupplementsScreen */
function bottleIndex(id: string): number {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash * 31 + id.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) % SUPPLEMENT_BOTTLES.length;
}

/**
 * Collects all searchable items across the app from existing data sources.
 * No new API calls — uses the content registry and static data.
 */
export function useSearchItems(): SearchItem[] {
  const { locale } = useLocale();
  const t = useTranslations();
  return useMemo(() => {
    const items: SearchItem[] = [];

    // ── Articles ──
    const moonBlocks = getBlocksByTag("moon").filter(
      (b): b is ContentBlock<"article"> => b.kind === "article",
    );
    const sharedBlocks = getBlocksByKind("article").filter(
      (b) => !b.tags.includes("moon" as ContentTag),
    ) as ContentBlock<"article">[];

    for (const block of [...moonBlocks, ...sharedBlocks]) {
      items.push({
        id: `article-${block.id}`,
        category: "articles",
        title: block.title,
        subtitle: block.subtitle,
        keywords: block.tags.join(" "),
        navTarget: {
          tab: "Learn",
          screen: "ArticleDetail",
          params: { articleId: block.id },
        },
      });
    }

    // ── Tests ──
    for (const block of getBlocksByKind("test")) {
      const body = block.body;
      const target = getTestRouteTarget(block.id as string);
      items.push({
        id: `test-${block.id}`,
        category: "tests",
        title: block.title,
        subtitle: block.subtitle ?? body.whatItMeasures,
        keywords: [
          body.testName,
          block.subtitle,
          body.whatItMeasures,
          block.tags.join(" "),
        ]
          .filter(Boolean)
          .join(" "),
        navTarget: {
          tab: "Access",
          screen: target.screen,
          params: target.params,
        },
      });
    }

    // ── Metric fact sheets ──
    for (const { key, metric, keywords } of getMetricFactsheets(locale)) {
      items.push({
        id: `metric-${key}`,
        category: "tests",
        title: metric.label,
        subtitle: metric.description,
        keywords: [
          metric.label,
          metric.description,
          metric.whyItMatters,
          metric.normalRange,
          keywords,
        ].join(" "),
        navTarget: {
          tab: "Care",
          screen: "MetricDetail",
          params: { metricKey: key },
        },
      });
    }

    // ── Medications (from encyclopedia) ──
    for (const block of getBlocksByKind("medication")) {
      const body = block.body;
      items.push({
        id: `medication-${block.id}`,
        category: "medications",
        title: block.title,
        subtitle:
          body.purpose.slice(0, 80) + (body.purpose.length > 80 ? "…" : ""),
        keywords: [body.genericName, body.drugClass, block.tags.join(" ")].join(
          " ",
        ),
        navTarget: {
          tab: "Learn",
          screen: "Medications",
        },
      });
    }

    // ── Procedures (from encyclopedia) ──
    for (const block of getBlocksByKind("procedure")) {
      const body = block.body;
      items.push({
        id: `procedure-${block.id}`,
        category: "procedures",
        title: block.title,
        subtitle: body.description,
        keywords: [body.procedureName, block.tags.join(" ")].join(" "),
        navTarget: {
          tab: "Learn",
          screen: "Procedures",
        },
      });
    }

    // ── Conditions (from encyclopedia) ──
    for (const block of getBlocksByKind("condition")) {
      const body = block.body;
      items.push({
        id: `condition-${block.id}`,
        category: "conditions",
        title: block.title,
        subtitle:
          body.description.slice(0, 80) +
          (body.description.length > 80 ? "…" : ""),
        keywords: [
          body.conditionName,
          body.category,
          block.tags.join(" "),
        ].join(" "),
        navTarget: {
          tab: "Learn",
          screen: "Conditions",
        },
      });
    }

    // ── Programs (with emoji icon) ──
    for (const block of getBlocksByKind("program")) {
      items.push({
        id: `program-${block.id}`,
        category: "programs",
        title: block.title,
        subtitle: block.subtitle,
        keywords: block.tags.join(" "),
        image: block.icon
          ? {
              type: "initials",
              initials: block.icon,
              bgColor: EMBER.colors.teal,
            }
          : undefined,
        navTarget: {
          tab: "Access",
          screen: "ProgramDetail",
          params: { programId: block.id },
        },
      });
    }

    // ── Supplements (with bottle images) ──
    for (const block of getBlocksByKind("supplement")) {
      const body = block.body;
      const bottle = SUPPLEMENT_BOTTLES[bottleIndex(block.id)]!;
      items.push({
        id: `supplement-${block.id}`,
        category: "supplements",
        title: block.title,
        subtitle: body.dosageRange,
        keywords: [
          body.name,
          body.evidenceStrength,
          body.forWhom,
          block.tags.join(" "),
        ].join(" "),
        image: { type: "require", source: bottle },
        navTarget: {
          tab: "Access",
          screen: "SupplementDetail",
          params: { supplementId: block.id },
        },
      });
    }

    // ── Therapies (with illustration images) ──
    for (const block of getBlocksByKind("therapy")) {
      const body = block.body;
      const illustration = THERAPY_ILLUSTRATIONS[block.id];
      items.push({
        id: `therapy-${block.id}`,
        category: "therapies",
        title: block.title,
        subtitle: body.description,
        keywords: [
          body.name,
          body.category,
          body.forWhom,
          block.tags.join(" "),
        ].join(" "),
        image: illustration
          ? { type: "require", source: illustration as number }
          : undefined,
        navTarget: {
          tab: "Access",
          screen: "TherapyDetail",
          params: { therapyId: block.id },
        },
      });
    }

    // ── Grants (with favicon) ──
    for (const grant of GRANTS) {
      items.push({
        id: `grant-${grant.id}`,
        category: "grants",
        title: grant.name,
        subtitle: grant.shortDescription,
        keywords: [
          grant.organization,
          grant.awardAmount,
          ...grant.treatmentsCovered,
        ].join(" "),
        image: grant.favicon ? { type: "uri", uri: grant.favicon } : undefined,
        navTarget: {
          tab: "Costs",
          screen: "GrantDetail",
          params: { grantId: grant.id },
        },
      });
    }

    // ── Document categories (with category icons) ──
    for (const doc of DOC_CATEGORIES) {
      items.push({
        id: `doc-${doc.key}`,
        category: "documents",
        title: t(doc.labelKey),
        image: { type: "require", source: doc.image },
        navTarget: {
          tab: "Care",
          screen: "Documents",
          params: { initialCategory: doc.key },
        },
      });
    }

    return items;
  }, [locale, t]);
}
